import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { demoAPI } from '../api';
import SettingsWelcomeField from '../components/SettingsWelcomeField';
import LiveClientPreview from '../components/LiveClientPreview';
import { getTranslation } from '../i18n';

// Demo Template Client View Texts are grouped by client-view section, in
// the same vertical order the user's client experiences them. Each field
// keeps its (stable) key — the migration-safe semantic from
// branding_resolver._client_texts is unchanged. We're only restructuring
// the editing surface so editing the demo feels like walking through the
// client journey, not auditing a database table.
const DEMO_SECTIONS = [
  {
    key: 'welcome',
    title: 'A. Welcome',
    helper: 'The first thing your client sees when they open the demo moodboard.',
    fields: [
      { key: 'welcome_title',       label: 'Welcome title',                  i18n: 'welcome',      multiline: false },
      { key: 'welcome_subtitle',    label: 'Description',                    i18n: 'client_intro', multiline: true  },
      { key: 'welcome_description', label: 'Hint under description (italic)', i18n: 'client_hint', multiline: false },
      { key: 'custom_message',      label: 'Custom message',                 i18n: null,           multiline: true  },
    ],
  },
  {
    key: 'instruction',
    title: 'B. Instruction',
    helper: 'A calm framing line and the reaction-button hint shown above the gallery.',
    fields: [
      { key: 'instruction_text',  label: 'Instruction text', i18n: 'instruction_main',  multiline: true  },
      { key: 'instruction_react', label: 'Reaction hint',    i18n: 'instruction_react', multiline: false },
    ],
  },
  {
    key: 'inspirations',
    title: 'C. Inspirations',
    helper: 'Heading for the optional client uploads section, when the demo board enables it.',
    fields: [
      { key: 'inspiration_title',       label: 'Inspirations title',       i18n: 'your_inspirations', multiline: false },
      { key: 'inspiration_description', label: 'Inspirations description', i18n: 'inspiration_intro', multiline: true  },
    ],
  },
  {
    key: 'notes',
    title: 'D. Notes',
    helper: 'Heading for the shared notes thread.',
    fields: [
      { key: 'notes_title',       label: 'Notes title',       i18n: 'notes_title',    multiline: false },
      { key: 'notes_description', label: 'Notes description', i18n: 'notes_subtitle', multiline: false },
    ],
  },
  {
    key: 'documents',
    title: 'E. Documents',
    helper: 'Heading for the documents area, when the demo board includes any.',
    fields: [
      { key: 'documents_title',       label: 'Documents title',       i18n: 'documents_title',    multiline: false },
      { key: 'documents_description', label: 'Documents description', i18n: 'documents_subtitle', multiline: false },
    ],
  },
];

// Flat list of fields (for default-text resolution and i18n base lookup).
const DEMO_TEXT_FIELDS = DEMO_SECTIONS.flatMap(s => s.fields);

/**
 * AdminDemoExperience — admin tab for managing the platform demo experience.
 *
 * Two cards:
 *  1) Demo Template Board — pick which of the admin's boards is cloned to new users.
 *  2) Editorial Guidance Copy — edit the calm full-width passages shown at
 *     intentional moments (welcome, demo gallery, library empty state).
 *
 * The aesthetic deliberately matches the rest of the admin panel: minimal,
 * no SaaS chips, generous whitespace.
 */
const PLACEMENT_META = [
  {
    key: 'welcome_intro',
    label: 'Dashboard welcome',
    where: 'Shown once on the dashboard for new users.',
  },
  {
    key: 'demo_intro',
    label: 'Demo board · above gallery',
    where: 'Shown only on the demo board, above the images.',
  },
  {
    key: 'library_intro',
    label: 'Image library · empty state',
    where: 'Shown when the user\u2019s image library is empty.',
  },
];

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'de', label: 'Deutsch' },
];

const labelStyle = {
  display: 'block',
  fontFamily: 'Playfair Display, serif',
  fontSize: 10,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'var(--kahla-text-light)',
  marginBottom: 8,
  fontWeight: 500,
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid var(--kahla-border)',
  background: '#fff',
  fontFamily: 'inherit',
  fontSize: 14,
  color: 'var(--kahla-charcoal)',
  outline: 'none',
};

const AdminDemoExperience = () => {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [templateBoard, setTemplateBoard] = useState(null);
  const [guidance, setGuidance] = useState({});
  const [activeLang, setActiveLang] = useState('en');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const [lastRefreshSummary, setLastRefreshSummary] = useState(null);
  // Demo template texts (lives on template_board.custom_texts) — fully
  // separate from the admin's owner-level branding overrides.
  const [demoTextsByLang, setDemoTextsByLang] = useState({ en: {}, pl: {}, de: {} });
  const [savingDemoTexts, setSavingDemoTexts] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [tplRes, gRes, txtRes] = await Promise.all([
        demoAPI.adminGetTemplate(),
        demoAPI.getGuidance(),
        demoAPI.adminGetTexts(),
      ]);
      setBoards(tplRes.data.boards || []);
      setTemplateId(tplRes.data.template_id || '');
      setTemplateBoard(tplRes.data.template || null);
      setGuidance(gRes.data?.guidance || {});
      const t = txtRes.data?.texts || { en: {}, pl: {}, de: {} };
      setDemoTextsByLang({
        en: t.en || {},
        pl: t.pl || {},
        de: t.de || {},
      });
    } catch (e) {
      // non-fatal
    } finally {
      setLoading(false);
    }
  };

  const showToast = (text, kind = 'success') => {
    setToast({ text, kind });
    setTimeout(() => setToast(null), 2500);
  };

  const handleSelectTemplate = async (boardId) => {
    setSavingTemplate(true);
    try {
      await demoAPI.adminSetTemplate(boardId || null);
      showToast('Demo template updated.');
      await load();
    } catch (e) {
      showToast('Could not update template.', 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshing(true);
    setConfirmRefresh(false);
    try {
      const { data } = await demoAPI.adminRefreshAll();
      setLastRefreshSummary(data);
      const errs = (data.errors || []).length;
      if (errs > 0) {
        showToast(`Refreshed ${data.refreshed} · ${errs} error(s)`, 'error');
      } else {
        showToast(`Refreshed ${data.refreshed} user demo${data.refreshed === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      showToast('Could not refresh user demos.', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const handleEditField = (placement, lang, field, value) => {
    setGuidance((prev) => {
      const next = { ...prev };
      next[placement] = { ...(next[placement] || {}) };
      next[placement][lang] = { ...(next[placement][lang] || {}), [field]: value };
      return next;
    });
  };

  // ---- Demo template texts helpers ----
  const updateDemoText = (key, value) => {
    setDemoTextsByLang((prev) => ({
      ...prev,
      [activeLang]: { ...(prev[activeLang] || {}), [key]: value },
    }));
  };

  // i18n default for a given field+language. Falls back EN if the language
  // has no translation for that key. Used as the "Default" the admin can
  // reset to and to flag fields as "Custom".
  const demoDefaultText = (key, lang = activeLang) => {
    const meta = DEMO_TEXT_FIELDS.find((f) => f.key === key);
    if (!meta || !meta.i18n) return '';
    const tForLang = getTranslation(lang) || {};
    if (tForLang[meta.i18n]) return tForLang[meta.i18n];
    const tEn = getTranslation('en') || {};
    return tEn[meta.i18n] || '';
  };

  // "Custom" semantic — mirrors the workspace branding flow:
  //   • undefined / null  → untouched, field is showing the default
  //   • ""                → intentional empty override (custom!)
  //   • any other string  → custom if it differs from the default
  // Keeping `""` as "custom" means the calm "Use default" affordance and
  // the visible "Custom" badge both surface for an intentionally blanked
  // field, so the admin can see at a glance "I deliberately left this
  // empty" instead of confusing it with the unchanged template default.
  const isDemoFieldCustom = (key) => {
    const v = (demoTextsByLang[activeLang] || {})[key];
    if (v === undefined || v === null) return false;
    return v !== (demoDefaultText(key) || '');
  };

  const resetDemoFieldToDefault = (key) => {
    updateDemoText(key, demoDefaultText(key) || '');
  };

  // Live preview wants the i18n base as the `templateTexts` layer so
  // empty admin overrides cleanly fall back to the real on-screen copy
  // every client actually sees. Built per-language from the demo field
  // catalogue; pure values, no React state.
  const templateTextsForPreview = (() => {
    const out = {};
    for (const lang of ['en', 'pl', 'de']) {
      const bucket = {};
      for (const f of DEMO_TEXT_FIELDS) {
        if (!f.i18n) continue;
        const t = getTranslation(lang) || {};
        const tEn = getTranslation('en') || {};
        const val = t[f.i18n] || tEn[f.i18n] || '';
        if (val) bucket[f.key] = val;
      }
      out[lang] = bucket;
    }
    return out;
  })();

  const handleSaveDemoTexts = async () => {
    if (!templateId) {
      showToast('Select a demo template board first.', 'error');
      return;
    }
    setSavingDemoTexts(true);
    try {
      await demoAPI.adminUpdateTexts(demoTextsByLang);
      showToast('Demo texts saved.');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save demo texts.';
      showToast(msg, 'error');
    } finally {
      setSavingDemoTexts(false);
    }
  };

  const handleSaveGuidance = async () => {
    setSaving(true);
    try {
      // Send as { placement: { lang: { title, body } } }, empty strings → fall back to default
      await demoAPI.adminUpdateGuidance(guidance);
      showToast('Guidance saved.');
    } catch (e) {
      showToast('Could not save guidance.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40 }}><div className="spinner" /></div>;
  }

  return (
    <div data-testid="admin-demo-experience" style={{ display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 980 }}>
      {toast && (
        <div
          data-testid="demo-toast"
          style={{
            position: 'fixed', top: 80, right: 28, zIndex: 9999,
            background: toast.kind === 'error' ? '#7a3a3a' : 'var(--kahla-charcoal)',
            color: '#fff', padding: '11px 22px',
            fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}
        >
          {toast.text}
        </div>
      )}

      {/* Header */}
      <header>
        <p style={{
          fontFamily: 'Playfair Display, serif', fontSize: 11,
          letterSpacing: '0.32em', textTransform: 'uppercase',
          color: 'var(--kahla-text-light)', margin: '0 0 10px', fontWeight: 300,
        }}>Demo Experience</p>
        <h2 style={{
          fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 400,
          color: 'var(--kahla-charcoal)', margin: '0 0 8px', lineHeight: 1.2,
        }}>
          Shape what new users see first.
        </h2>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
          fontSize: 16, color: 'var(--kahla-text-light)', margin: 0, lineHeight: 1.65, maxWidth: 640,
        }}>
          Choose one of your boards to be cloned into every new account, and edit the editorial passages shown at intentional moments.
        </p>
      </header>

      {/* CARD 1 — Demo template board */}
      <section
        data-testid="demo-template-card"
        style={{ border: '1px solid var(--kahla-border)', padding: 32, background: '#fff' }}
      >
        <h3 style={{
          fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 400,
          color: 'var(--kahla-charcoal)', margin: '0 0 6px',
        }}>Demo template board</h3>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
          fontSize: 14, color: 'var(--kahla-text-light)', margin: '0 0 22px', lineHeight: 1.6,
        }}>
          The selected board (and its images, labels, reactions, finals, notes and documents) will be cloned into every new user&rsquo;s workspace.
        </p>

        <label style={labelStyle}>Selected board</label>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            data-testid="demo-template-select"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            disabled={savingTemplate}
            style={{ ...inputStyle, maxWidth: 460 }}
          >
            <option value="">— None (use built-in fallback) —</option>
            {boards.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}{b.client_name ? ` · ${b.client_name}` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="demo-template-save-btn"
            onClick={() => handleSelectTemplate(templateId)}
            disabled={savingTemplate}
            style={{
              background: 'var(--kahla-charcoal)', color: '#fff', border: 'none',
              padding: '11px 22px',
              fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
              textTransform: 'uppercase', cursor: savingTemplate ? 'wait' : 'pointer',
              opacity: savingTemplate ? 0.6 : 1,
            }}
          >
            {savingTemplate ? 'Saving' : 'Save selection'}
          </button>
          {templateBoard && (
            <button
              type="button"
              data-testid="demo-template-edit-link"
              onClick={() => navigate(`/editor/${templateBoard.id}`)}
              style={{
                background: 'none', border: 'none', padding: '4px 0',
                fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: 'var(--kahla-charcoal)',
                textDecoration: 'underline', textUnderlineOffset: 4, cursor: 'pointer',
              }}
            >
              Edit template board →
            </button>
          )}
        </div>

        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
          fontSize: 13, color: 'var(--kahla-text-light)',
          margin: '20px 0 0', lineHeight: 1.6, maxWidth: 620,
        }}>
          Tip: keep the selected board in your own workspace. Edit it normally — every change cascades to future new users automatically.
        </p>

        {/* Bulk refresh — rebuild every existing user's demo board from the
            current template. Useful after substantive template edits. */}
        <div
          data-testid="demo-refresh-block"
          style={{
            marginTop: 28,
            paddingTop: 22,
            borderTop: '1px solid var(--kahla-border)',
          }}
        >
          <label style={labelStyle}>Cascade to existing users</label>
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
            fontSize: 14, color: 'var(--kahla-text-light)',
            margin: '0 0 14px', lineHeight: 1.6, maxWidth: 620,
          }}>
            Saving the selection above only affects future signups. Use this to rebuild every existing user&rsquo;s demo board from the currently selected template — their old demo is replaced with a fresh clone.
          </p>

          {!confirmRefresh ? (
            <button
              type="button"
              data-testid="demo-refresh-all-btn"
              onClick={() => setConfirmRefresh(true)}
              disabled={refreshing || !templateId}
              title={!templateId ? 'Select a template first' : ''}
              style={{
                background: 'transparent',
                color: 'var(--kahla-charcoal)',
                border: '1px solid var(--kahla-charcoal)',
                padding: '11px 22px',
                fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
                textTransform: 'uppercase',
                cursor: (refreshing || !templateId) ? 'not-allowed' : 'pointer',
                opacity: (refreshing || !templateId) ? 0.45 : 1,
                transition: 'background 0.2s, color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (refreshing || !templateId) return;
                e.currentTarget.style.background = 'var(--kahla-charcoal)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--kahla-charcoal)';
              }}
            >
              Refresh all user demos
            </button>
          ) : (
            <div
              data-testid="demo-refresh-confirm"
              style={{
                background: 'var(--kahla-cream-light)',
                border: '1px solid var(--kahla-border)',
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                flexWrap: 'wrap',
              }}
            >
              <span style={{
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic',
                fontSize: 14,
                color: 'var(--kahla-charcoal)',
                flex: 1,
                minWidth: 220,
              }}>
                Replace every existing user&rsquo;s demo board? This cannot be undone.
              </span>
              <button
                type="button"
                data-testid="demo-refresh-confirm-cancel"
                onClick={() => setConfirmRefresh(false)}
                disabled={refreshing}
                style={{
                  background: 'transparent', border: 'none',
                  fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.14em',
                  textTransform: 'uppercase', cursor: 'pointer',
                  color: 'var(--kahla-text-light)', padding: '6px 10px',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="demo-refresh-confirm-yes"
                onClick={handleRefreshAll}
                disabled={refreshing}
                style={{
                  background: 'var(--kahla-charcoal)', color: '#fff', border: 'none',
                  padding: '10px 20px',
                  fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
                  textTransform: 'uppercase', cursor: refreshing ? 'wait' : 'pointer',
                  opacity: refreshing ? 0.6 : 1,
                }}
              >
                {refreshing ? 'Refreshing…' : 'Yes, replace all'}
              </button>
            </div>
          )}

          {lastRefreshSummary && (
            <p
              data-testid="demo-refresh-summary"
              style={{
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic',
                fontSize: 13,
                color: 'var(--kahla-text-light)',
                margin: '14px 0 0',
              }}
            >
              Last run: refreshed {lastRefreshSummary.refreshed} · skipped {lastRefreshSummary.skipped}
              {(lastRefreshSummary.errors || []).length > 0
                ? ` · ${lastRefreshSummary.errors.length} error(s)`
                : ''}
            </p>
          )}
        </div>
      </section>

      {/* CARD 1b — Demo template client view texts (separate from admin's
          owner-level branding overrides) */}
      <section
        data-testid="demo-texts-card"
        style={{ border: '1px solid var(--kahla-border)', padding: 32, background: '#fff' }}
      >
        <h3 style={{
          fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 400,
          color: 'var(--kahla-charcoal)', margin: '0 0 6px',
        }}>Demo template client view texts</h3>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
          fontSize: 14, color: 'var(--kahla-text-light)', margin: '0 0 6px', lineHeight: 1.6,
        }}>
          What every cloned demo board says, in three languages. Saved on the template board only &mdash; entirely separate from your own private branding settings.
        </p>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontSize: 11, color: 'var(--kahla-text-light)',
          margin: '0 0 22px', lineHeight: 1.6, letterSpacing: '0.04em',
        }}>
          New signups receive these immediately. To cascade edits to existing user demos, use <em>&laquo;Refresh all user demos&raquo;</em> above.
        </p>

        {!templateId && (
          <div
            data-testid="demo-texts-no-template-hint"
            style={{
              padding: '14px 18px',
              background: 'var(--kahla-cream-light)',
              border: '1px solid var(--kahla-border)',
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: 14,
              color: 'var(--kahla-text-light)',
              marginBottom: 18,
            }}
          >
            Select a demo template board above before editing demo texts.
          </div>
        )}

        {/* Language tabs (reuse the same activeLang as the guidance card) */}
        <div
          role="tablist"
          style={{
            display: 'flex', gap: 0, borderBottom: '1px solid var(--kahla-border)',
            marginBottom: 24,
          }}
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              role="tab"
              data-testid={`demo-texts-lang-${l.code}`}
              onClick={() => setActiveLang(l.code)}
              style={{
                padding: '10px 18px',
                background: 'none',
                border: 'none',
                borderBottom: `2px solid ${activeLang === l.code ? 'var(--kahla-charcoal)' : 'transparent'}`,
                fontFamily: 'inherit',
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: activeLang === l.code ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div
          data-testid="demo-texts-sections"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 32,
            marginBottom: 28,
          }}
        >
          {DEMO_SECTIONS.map((section) => (
            <div
              key={section.key}
              data-testid={`demo-texts-section-${section.key}`}
              style={{
                paddingBottom: 28,
                borderBottom: '1px solid var(--kahla-border)',
              }}
            >
              <h4 style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: 14,
                fontWeight: 400,
                color: 'var(--kahla-charcoal)',
                margin: '0 0 4px',
                letterSpacing: '0.04em',
              }}>
                {section.title}
              </h4>
              <p style={{
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic',
                fontSize: 13,
                color: 'var(--kahla-text-light)',
                margin: '0 0 18px',
                lineHeight: 1.55,
              }}>
                {section.helper}
              </p>

              {/* One field per row, full width — no two-column grid.
                  The admin reads downward through the section the same
                  way the client reads downward through the moodboard. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {section.fields.map((f) => (
                  <SettingsWelcomeField
                    key={f.key}
                    label={f.label}
                    testId={`demo-text-${f.key}`}
                    value={(demoTextsByLang[activeLang] || {})[f.key] ?? ''}
                    onChange={(v) => updateDemoText(f.key, v)}
                    defaultValue={demoDefaultText(f.key)}
                    isCustom={isDemoFieldCustom(f.key)}
                    onUseDefault={() => resetDemoFieldToDefault(f.key)}
                    multiline={f.multiline}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── Live preview ─────────────────────────────────────────────
            Stays clearly labelled as "preview" so admins don't mistake
            it for editable content. Uses the exact resolveText chain
            the public ClientView uses, so empty overrides render as
            true blanks here — matching what the client will see. */}
        <div
          data-testid="demo-texts-preview"
          style={{ marginTop: 4, marginBottom: 28 }}
        >
          <p style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            margin: '0 0 10px',
            fontWeight: 500,
          }}>
            Preview · {LANGS.find(l => l.code === activeLang)?.label || activeLang}
          </p>
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: 13,
            color: 'var(--kahla-text-light)',
            margin: '0 0 14px',
            lineHeight: 1.6,
          }}>
            A faithful miniature of the client view. Intentionally blank fields stay blank.
          </p>
          <div style={{
            border: '1px solid var(--kahla-border)',
            background: 'var(--kahla-cream-light, #faf8f5)',
            padding: 0,
            overflow: 'hidden',
          }}>
            <LiveClientPreview
              colors={{
                page:      '#faf8f5',
                section:   '#ffffff',
                border:    '#ece9e4',
                text:      '#4a4540',
                textLight: '#8a847b',
                accent:    '#b8a992',
              }}
              brandName="Maestra"
              brandingDisplayMode="name_only"
              logoSize="M"
              welcomeByLang={demoTextsByLang}
              templateTexts={templateTextsForPreview}
              editLang={activeLang}
              t={getTranslation(activeLang)}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            data-testid="demo-texts-save-btn"
            onClick={handleSaveDemoTexts}
            disabled={savingDemoTexts || !templateId}
            style={{
              background: 'var(--kahla-charcoal)',
              color: '#fff',
              border: 'none',
              padding: '11px 26px',
              fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
              textTransform: 'uppercase',
              cursor: (savingDemoTexts || !templateId) ? 'wait' : 'pointer',
              opacity: (savingDemoTexts || !templateId) ? 0.5 : 1,
            }}
          >
            {savingDemoTexts ? 'Saving' : 'Save demo texts'}
          </button>
        </div>
      </section>

      {/* CARD 2 — Editorial guidance copy */}
      <section
        data-testid="demo-guidance-card"
        style={{ border: '1px solid var(--kahla-border)', padding: 32, background: '#fff' }}
      >
        <h3 style={{
          fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 400,
          color: 'var(--kahla-charcoal)', margin: '0 0 6px',
        }}>Editorial guidance copy</h3>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
          fontSize: 14, color: 'var(--kahla-text-light)', margin: '0 0 22px', lineHeight: 1.6,
        }}>
          Calm, full-width passages shown at three intentional moments. Speak about the transformation, never the feature. Empty fields fall back to the platform default.
        </p>

        {/* Language tabs */}
        <div
          role="tablist"
          style={{
            display: 'flex', gap: 0, borderBottom: '1px solid var(--kahla-border)',
            marginBottom: 24,
          }}
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              role="tab"
              data-testid={`demo-guidance-lang-${l.code}`}
              onClick={() => setActiveLang(l.code)}
              style={{
                background: 'none', border: 'none', padding: '10px 18px',
                fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.14em',
                textTransform: 'uppercase',
                borderBottom: activeLang === l.code ? '2px solid var(--kahla-charcoal)' : '2px solid transparent',
                color: activeLang === l.code ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                cursor: 'pointer', marginBottom: -1,
              }}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {PLACEMENT_META.map((p) => {
            const fields = (guidance[p.key] && guidance[p.key][activeLang]) || {};
            return (
              <div key={p.key} data-testid={`demo-guidance-placement-${p.key}`}>
                <label style={labelStyle}>{p.label}</label>
                <p style={{
                  fontSize: 12, color: 'var(--kahla-text-light)', margin: '0 0 12px',
                  lineHeight: 1.6, fontStyle: 'italic',
                }}>
                  {p.where}
                </p>

                <input
                  type="text"
                  data-testid={`demo-guidance-${p.key}-${activeLang}-title`}
                  value={fields.title || ''}
                  placeholder="Title (e.g. A calmer way to guide decisions.)"
                  onChange={(e) => handleEditField(p.key, activeLang, 'title', e.target.value)}
                  style={{ ...inputStyle, marginBottom: 8 }}
                />
                <textarea
                  data-testid={`demo-guidance-${p.key}-${activeLang}-body`}
                  value={fields.body || ''}
                  placeholder="Body (one or two soft sentences — the transformation, not the feature)."
                  rows={3}
                  onChange={(e) => handleEditField(p.key, activeLang, 'body', e.target.value)}
                  style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 28 }}>
          <button
            type="button"
            data-testid="demo-guidance-save-btn"
            onClick={handleSaveGuidance}
            disabled={saving}
            style={{
              background: 'var(--kahla-charcoal)', color: '#fff', border: 'none',
              padding: '11px 22px',
              fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
              textTransform: 'uppercase', cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving' : 'Save guidance'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default AdminDemoExperience;
